// src/components/ModalPetDetail.js

import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPetById } from '../api/queries';
// >>> Eliminado react-swipeable y lógica manual de swipe
import './ModalPetDetail.css';
// >>> Importamos el carrusel reutilizable
import EmblaCarousel from '../components/EmblaCarousel';
// ➕ Importamos nuestro LikeButton unificado con React Query
import LikeButton from '../components/LikeButton';
import ShareModal from './ShareModal';
import ModalProfesionalDetail from '../components/ModalProfesionalDetail';
import ModalPetDetailSkeleton from '../skeletons/ModalPetDetailSkeleton';
import { ModalContext } from '../context/ModalContext';
import { TabContext }   from '../context/TabContext';
import { AuthContext }  from '../context/AuthContext';
import { SlPaperPlane } from 'react-icons/sl';

/**
 * Calcula edad a partir de fecha de nacimiento.
 */
function calculateAge(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const now   = new Date();
  let years   = now.getFullYear() - birth.getFullYear();
  let months  = now.getMonth() - birth.getMonth();
  let days    = now.getDate() - birth.getDate();
  if (days < 0) months--;
  if (months < 0) {
    years--;
    months += 12;
  }
  if (years < 1) {
    return { type: 'months', value: Math.max(months, 1) };
  }
  return { type: 'years', value: years };
}

/**
 * ModalPetDetail
 * @param {string|number} petId — Id para fetch si no se recibe `pet`.
 */
export default function ModalPetDetail({ petId }) {
  const { openModal, closeModal } = useContext(ModalContext);
  const { activeTab }             = useContext(TabContext);
  const { user }                  = useContext(AuthContext);

  // ➤ React Query v5: obtener detalle de mascota
  const {
    data: pet,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['pet', petId],
    queryFn: () => fetchPetById(petId),
    staleTime: 15 * 60_000,    // 5 minutos en caché antes de refetch
    cacheTime: 30 * 60_000    // 30 minutos en memoria
  });

  // Skeleton mientras carga datos
  if (isLoading) {
    return <ModalPetDetailSkeleton />;
  }

  // Mostrar error si falla el fetch
  if (isError) {
    console.error('[ModalPetDetail] Error fetching pet:', error);
    return <p>Error al cargar detalles de la mascota.</p>;
  }

  const photos = pet.fotos || [];
  const truncatedHistoria = pet.historia || '';

  /** Abre modal de compartir */
  const handleOpenShareModal = () => {
    openModal(
      <ShareModal
        onClose={() => closeModal(activeTab)}
        shareUrl={`${window.location.origin}/pet/${pet.id}?modal=true`}
        shareText={`¡Échale un vistazo a ${pet.nombre}, disponible en Pet_it!`}
      />,
      { tab: activeTab, keepHeader: true }
    );
  };

  /**
   * Abre modal de profesional SOLO si NO es usuario profesional.
   * Recibe el evento para detener la propagación.
   */
  const handleOpenProfesionalDetail = e => {
    e.stopPropagation();
    if (!user?.is_professional) {
      openModal(
        <ModalProfesionalDetail professionalId={pet.profesional.id} />,
        {
          tab: activeTab,
          metadata: {
            profName: pet.profesional.nombre,
            profLogo: pet.profesional.logo,
            profId:   pet.profesional.id,
          },
          overlayClassName: 'pet-detail-overlay'
        }
      );
    }
  };

  return (
    <div className="modalCard" onClick={e => e.stopPropagation()}>
      {/* === Carrusel reutilizable === */}
      <EmblaCarousel
        photos={photos}
        heightDesktop="400px"
        heightMobile="250px"
        showArrows={false}
        showDots={photos.length > 0}
        loop={true}
        inViewThreshold={0.2}
      />

      {/* === Datos de la mascota === */}
      <div className="modalInfoRow">
        <div className="modalInfoLeft">
          {pet.f_nacimiento && (() => {
            const age = calculateAge(pet.f_nacimiento);
            return (
              <p className="modalAge">
                Edad:{' '}
                <strong>
                  {age.type === 'years'
                    ? `${age.value} año${age.value > 1 ? 's' : ''}`
                    : `${age.value} mes${age.value > 1 ? 'es' : ''}`}
                </strong>
              </p>
            );
          })()}
          {pet.raza1 && (
            <p className="modalRaza">
              Raza: <strong>{pet.raza1}</strong>
            </p>
          )}
          {pet.peso && (
            <p className="modalPeso">
              Peso: <strong>{pet.peso}</strong>
            </p>
          )}
          {pet.size && (
            <p className="modalSize">
              Tamaño: <strong>{pet.size}</strong>
            </p>
          )}
          {pet.ubicacion && (
            <p className="modalUbicacion">
              Ubicación: <strong>{pet.ubicacion}</strong>
            </p>
          )}
        </div>
        <div className="modalInfoRight">
          <button className="adoptButton">
            <img src="/adoptame.png" alt="Adóptame" className="adoptImg" />
          </button>
        </div>
      </div>

      <hr className="separatorLine" />

      {/* === Profesional, like y compartir === */}
      <div className="modalExtraInfoRow">
        <div
          className="modalProfessionalLink"
          onClick={handleOpenProfesionalDetail}  // ahora pasamos el evento correctamente
          style={{ cursor: 'pointer' }}
        >
          {pet.profesional && (
            <>
              <img
                src={pet.profesional.logo}
                alt={pet.profesional.nombre}
                className="modalProfessionalLogo"
                loading="lazy"
              />
              <span className="modalProfessionalName">
                {pet.profesional.nombre}
              </span>
            </>
          )}
        </div>

        {/* 3️⃣ SOLO usuarios NO profesionales pueden dar like */}
        {!user?.is_professional && (
          <div className="modalExtraButtons">
            {/* LikeButton ya vinculado a React Query */}
            <LikeButton
              petId={pet.id}
              initialIsFavorite={pet.liked_by_user}
            />
            <button
              className="modalShareButton"
              onClick={e => {
                e.stopPropagation();
                handleOpenShareModal();
              }}
            >
              <SlPaperPlane style={{ color: 'gray', fontSize: '1.5rem' }} />
            </button>
          </div>
        )}
      </div>

      {/* === Historia === */}
      <div className="modalPetHistory">
        <p>{truncatedHistoria}</p>
      </div>
    </div>
  );
}
